import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import accountingService from '../../services/accountingService';
import { Account, JournalEntry as JournalEntryType } from '../../types';

interface FormLineData {
  account_id: number;
  debit: string;
  credit: string;
  description?: string;
}

interface FormData {
  entry_date: string;
  reference: string;
  description: string;
  lines: FormLineData[];
}

const JournalEntry: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [entries, setEntries] = useState<JournalEntryType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [formData, setFormData] = useState<FormData>({
    entry_date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    lines: [
      { account_id: 0, debit: '', credit: '', description: '' },
      { account_id: 0, debit: '', credit: '', description: '' },
    ],
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      setLoading(true);
      const [accountsRes, entriesRes] = await Promise.all([
        accountingService.getAccounts(),
        accountingService.getJournalEntries()
      ]);
      setAccounts((accountsRes.data || []).filter((acc) => acc.is_active));
      setEntries(entriesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setAccounts([]);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleLineChange = (
    index: number,
    field: keyof FormLineData,
    value: string | number
  ): void => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const addLine = (): void => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { account_id: 0, debit: '', credit: '', description: '' }],
    }));
  };

  const removeLine = (index: number): void => {
    if (formData.lines.length <= 2) return;
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const calculateTotals = (): { totalDebit: number; totalCredit: number } => {
    const totalDebit = formData.lines.reduce(
      (sum, line) => sum + (parseFloat(line.debit as string) || 0),
      0
    );
    const totalCredit = formData.lines.reduce(
      (sum, line) => sum + (parseFloat(line.credit as string) || 0),
      0
    );
    return { totalDebit, totalCredit };
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const { totalDebit, totalCredit } = calculateTotals();

    if (!formData.entry_date) {
      newErrors.entry_date = 'Date is required';
    }

    if (!formData.description) {
      newErrors.description = 'Description is required';
    }

    if (totalDebit !== totalCredit) {
      newErrors.balance = `Debits ($${totalDebit.toFixed(2)}) must equal Credits ($${totalCredit.toFixed(2)})`;
    }

    if (totalDebit === 0 || totalCredit === 0) {
      newErrors.balance = 'Entry must have at least one debit and one credit';
    }

    formData.lines.forEach((line, index) => {
      if (!line.account_id) {
        newErrors[`line_${index}_account`] = 'Account is required';
      }
      if (!line.debit && !line.credit) {
        newErrors[`line_${index}_amount`] = 'Either debit or credit is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await accountingService.createJournalEntry({
        entry_date: formData.entry_date,
        reference_number: formData.reference,
        description: formData.description,
        lines: formData.lines.map((line) => ({
          account_id: line.account_id,
          debit_amount: parseFloat(line.debit) || 0,
          credit_amount: parseFloat(line.credit) || 0,
          description: line.description,
        })),
      });

      // Close dialog and reset form
      setShowDialog(false);
      setFormData({
        entry_date: new Date().toISOString().split('T')[0],
        reference: '',
        description: '',
        lines: [
          { account_id: 0, debit: '', credit: '', description: '' },
          { account_id: 0, debit: '', credit: '', description: '' },
        ],
      });
      setErrors({});
      
      // Reload entries
      loadData();
    } catch (error) {
      console.error('Error creating journal entry:', error);
      setErrors({ submit: 'Failed to create journal entry' });
    }
  };

  const { totalDebit, totalCredit } = calculateTotals();
  const isBalanced = totalDebit === totalCredit && totalDebit > 0;

  const filteredEntries = entries.filter(entry => {
    if (filterStatus === 'all') return true;
    return entry.status === filterStatus;
  });

  const getStatusColor = (status: string): string => {
    return status === 'posted' ? '#2E7D32' : '#ED6C02';
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>Loading journal entries...</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/accounting')}>← Back</BackButton>
          <div>
            <PageTitle>Journal Entries</PageTitle>
            <PageSubtitle>Record and review double-entry accounting transactions</PageSubtitle>
          </div>
        </HeaderLeft>
        <HeaderRight>
          <PrimaryButton onClick={() => setShowDialog(true)}>
            <ButtonIcon>+</ButtonIcon>
            New Entry
          </PrimaryButton>
        </HeaderRight>
      </PageHeader>

      <FilterSection>
        <FilterLabel>Filter by Status:</FilterLabel>
        <FilterChip 
          $active={filterStatus === 'all'} 
          onClick={() => setFilterStatus('all')}
        >
          All ({entries.length})
        </FilterChip>
        <FilterChip 
          $active={filterStatus === 'draft'} 
          onClick={() => setFilterStatus('draft')}
        >
          Draft ({entries.filter(e => e.status === 'draft').length})
        </FilterChip>
        <FilterChip 
          $active={filterStatus === 'posted'} 
          onClick={() => setFilterStatus('posted')}
        >
          Posted ({entries.filter(e => e.status === 'posted').length})
        </FilterChip>
      </FilterSection>

      <ContentCard>
        {filteredEntries.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📝</EmptyIcon>
            <EmptyTitle>No journal entries found</EmptyTitle>
            <EmptyText>
              {filterStatus === 'all' 
                ? 'Create your first journal entry to get started' 
                : `No ${filterStatus} entries found`}
            </EmptyText>
            {filterStatus === 'all' && (
              <PrimaryButton onClick={() => setShowDialog(true)}>
                <ButtonIcon>+</ButtonIcon>
                Create Entry
              </PrimaryButton>
            )}
          </EmptyState>
        ) : (
          <EntriesTable>
            <thead>
              <tr>
                <Th>Entry #</Th>
                <Th>Date</Th>
                <Th>Description</Th>
                <Th>Type</Th>
                <Th>Period</Th>
                <Th style={{ textAlign: 'right' }}>Amount</Th>
                <Th>Status</Th>
                <Th>Created By</Th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <EntryRow key={entry.id}>
                  <Td><EntryNumber>{entry.entry_number}</EntryNumber></Td>
                  <Td>{new Date(entry.entry_date).toLocaleDateString()}</Td>
                  <Td><Description>{entry.description}</Description></Td>
                  <Td><TypeBadge>{entry.entry_type}</TypeBadge></Td>
                  <Td>{entry.period_name || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {entry.lines && entry.lines.length > 0 
                      ? entry.lines.reduce((sum, line) => sum + (parseFloat(String(line.debit_amount || 0))), 0).toFixed(2)
                      : '0.00'}
                  </Td>
                  <Td>
                    <StatusBadge $color={getStatusColor(entry.status)}>
                      {entry.status}
                    </StatusBadge>
                  </Td>
                  <Td>{entry.created_by_name || '-'}</Td>
                </EntryRow>
              ))}
            </tbody>
          </EntriesTable>
        )}
      </ContentCard>

      {/* Create Entry Dialog */}
      {showDialog && (
        <DialogOverlay onClick={() => setShowDialog(false)}>
          <DialogContainer onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>New Journal Entry</DialogTitle>
              <CloseButton onClick={() => setShowDialog(false)}>×</CloseButton>
            </DialogHeader>

            <DialogContent>
              <Form onSubmit={handleSubmit}>
                {errors.submit && <Alert>{errors.submit}</Alert>}

        <FormGrid>
          <FormGroup>
            <label htmlFor="entry_date">Entry Date *</label>
            <Input
              type="date"
              id="entry_date"
              name="entry_date"
              value={formData.entry_date}
              onChange={handleChange}
              $hasError={!!errors.entry_date}
            />
            {errors.entry_date && <ErrorText>{errors.entry_date}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <label htmlFor="reference">Reference Number</label>
            <Input
              type="text"
              id="reference"
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              placeholder="JE-001"
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <label htmlFor="description">Description *</label>
          <TextArea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            $hasError={!!errors.description}
            placeholder="Enter journal entry description..."
            rows={3}
          />
          {errors.description && <ErrorText>{errors.description}</ErrorText>}
        </FormGroup>

        <LinesSection>
          <SectionHeader>
            <h2>Journal Lines</h2>
            <AddLineButton type="button" onClick={addLine}>
              + Add Line
            </AddLineButton>
          </SectionHeader>

          {errors.balance && <BalanceError>{errors.balance}</BalanceError>}

          <LinesTable>
            <thead>
              <tr>
                <Th style={{ width: '30%' }}>Account</Th>
                <Th style={{ width: '30%' }}>Description</Th>
                <Th style={{ width: '15%' }}>Debit</Th>
                <Th style={{ width: '15%' }}>Credit</Th>
                <Th style={{ width: '10%' }}>Action</Th>
              </tr>
            </thead>
            <tbody>
              {formData.lines.map((line, index) => (
                <LineRow key={index}>
                  <Td>
                    <Select
                      value={line.account_id}
                      onChange={(e) =>
                        handleLineChange(index, 'account_id', parseInt(e.target.value))
                      }
                      $hasError={!!errors[`line_${index}_account`]}
                    >
                      <option value={0}>Select Account...</option>
                      {accounts.map((acc) => (
                        <option key={acc.account_id} value={acc.account_id}>
                          {acc.account_code} - {acc.account_name}
                        </option>
                      ))}
                    </Select>
                    {errors[`line_${index}_account`] && (
                      <ErrorText>{errors[`line_${index}_account`]}</ErrorText>
                    )}
                  </Td>
                  <Td>
                    <Input
                      type="text"
                      value={line.description || ''}
                      onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                      placeholder="Line description..."
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={line.debit}
                      onChange={(e) => handleLineChange(index, 'debit', e.target.value)}
                      disabled={!!line.credit}
                      placeholder="0.00"
                      $hasError={!!errors[`line_${index}_amount`]}
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={line.credit}
                      onChange={(e) => handleLineChange(index, 'credit', e.target.value)}
                      disabled={!!line.debit}
                      placeholder="0.00"
                      $hasError={!!errors[`line_${index}_amount`]}
                    />
                    {errors[`line_${index}_amount`] && (
                      <ErrorText>{errors[`line_${index}_amount`]}</ErrorText>
                    )}
                  </Td>
                  <Td>
                    <RemoveButton
                      type="button"
                      onClick={() => removeLine(index)}
                      disabled={formData.lines.length <= 2}
                    >
                      ✕
                    </RemoveButton>
                  </Td>
                </LineRow>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <TotalTd colSpan={2}>
                  <strong>Totals</strong>
                </TotalTd>
                <TotalTd $balanced={isBalanced}>
                  <strong>${totalDebit.toFixed(2)}</strong>
                </TotalTd>
                <TotalTd $balanced={isBalanced}>
                  <strong>${totalCredit.toFixed(2)}</strong>
                </TotalTd>
                <TotalTd>
                  {isBalanced ? (
                    <BalancedBadge>✓ Balanced</BalancedBadge>
                  ) : (
                    <UnbalancedBadge>⚠ Unbalanced</UnbalancedBadge>
                  )}
                </TotalTd>
              </tr>
            </tfoot>
          </LinesTable>
        </LinesSection>

        <FormActions>
          <CancelButton type="button" onClick={() => setShowDialog(false)}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={!isBalanced}>
            Create Entry
          </SubmitButton>
        </FormActions>
      </Form>
            </DialogContent>
          </DialogContainer>
        </DialogOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  background: #F8FAFB;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0066CC;
    color: #0066CC;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 4px 0 0 0;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #0066CC;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #0052A3;
  }

  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.span`
  font-size: 16px;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px 24px;
  background: white;
  border-radius: 8px;
`;

const FilterLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #6B7280;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${props => props.$active ? '#0066CC' : '#E5E7EB'};
  background: ${props => props.$active ? '#EBF5FF' : 'white'};
  color: ${props => props.$active ? '#0066CC' : '#6B7280'};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0066CC;
    color: #0066CC;
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const EntriesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EntryRow = styled.tr`
  border-bottom: 1px solid #F3F4F6;
  transition: background 0.15s;

  &:hover {
    background: #F9FAFB;
  }
`;

const LineRow = styled.tr`
  border-bottom: 1px solid #F3F4F6;
  
  &:hover {
    background: #F9FAFB;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1A1A1A;
`;

const EntryNumber = styled.span`
  font-family: monospace;
  font-weight: 600;
  color: #0066CC;
`;

const Description = styled.span`
  color: #4B5563;
`;

const TypeBadge = styled.span`
  padding: 4px 10px;
  background: #F3F4F6;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  text-transform: capitalize;
`;

const StatusBadge = styled.span<{ $color: string }>`
  padding: 4px 10px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  padding: 80px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 24px 0;
`;

const LoadingState = styled.div`
  padding: 60px 20px;
  text-align: center;
  font-size: 16px;
  color: #6B7280;
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const DialogContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #E5E7EB;
`;

const DialogTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1A1A1A;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6B7280;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #F3F4F6;
    color: #1A1A1A;
  }
`;

const DialogContent = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Form = styled.form`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: 600;
    color: #333;
    font-size: 14px;
  }
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => (props.$hasError ? '#ef4444' : '#e5e7eb')};
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? '#ef4444' : '#667eea')};
  }

  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => (props.$hasError ? '#ef4444' : '#e5e7eb')};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? '#ef4444' : '#667eea')};
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => (props.$hasError ? '#ef4444' : '#e5e7eb')};
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? '#ef4444' : '#667eea')};
  }
`;

const ErrorText = styled.span`
  color: #ef4444;
  font-size: 12px;
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
`;

const LinesSection = styled.div`
  margin: 24px 0;
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #1A1A1A;
    margin: 0;
  }
`;

const AddLineButton = styled.button`
  padding: 8px 16px;
  background: #EBF5FF;
  border: 1px solid #0066CC;
  border-radius: 6px;
  color: #0066CC;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #0066CC;
    color: white;
  }
`;

const TotalTd = styled.td<{ $balanced?: boolean }>`
  padding: 16px;
  font-size: 14px;
  background: #F9FAFB;
  border-top: 2px solid #E5E7EB;
  color: ${props => (props.$balanced === false ? '#EF4444' : '#1A1A1A')};
  font-weight: 600;
`;

const RemoveButton = styled.button`
  width: 32px;
  height: 32px;
  background: #FEE2E2;
  border: none;
  border-radius: 6px;
  color: #991B1B;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #FECACA;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const BalancedBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #D1FAE5;
  color: #065F46;
`;

const UnbalancedBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #FEF3C7;
  color: #92400E;
`;

const BalanceError = styled.div`
  padding: 12px 16px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 6px;
  color: #92400E;
  font-size: 14px;
  margin-bottom: 16px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  color: #1A1A1A;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F3F4F6;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: #0066CC;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #0052A3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default JournalEntry;
